import { useCallback, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { skipToken } from '@reduxjs/toolkit/query';
import {
  apiErrorText,
  useGetPtoRdSheetImageQuery,
  useGetPtoVolumeHistoryQuery,
} from '@shared/api';
import { Button } from '@shared/ui/Button';
import { looksUnrecognized } from '../rdEdits/logic';
import { RowEditPanel } from '../rdEdits/RowEditDialog';
import { useRdVolumeEditAccess } from '../rdEdits/useRdVolumeEditAccess';
import {
  SHEET_CHANNEL,
  SHEET_PROTOCOL,
  SHEET_ROUTE,
  isContinuation,
  parseSheetMessage,
  sheetHintOf,
  targetFromSearch,
  targetToSearch,
  type SheetMessage,
  type SheetTarget,
} from './protocol';
import { SheetView } from './SheetView';

const Page = styled.div`
  height: 100vh;
  display: flex;
  flex-direction: column;
  background: ${({ theme }) => theme.colors.background};
  color: ${({ theme }) => theme.colors.text.primary};
`;

const Head = styled.header`
  display: flex;
  align-items: flex-start;
  gap: ${({ theme }) => theme.spacing(3)};
  padding: ${({ theme }) => theme.spacing(2.5)} ${({ theme }) => theme.spacing(3)};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  background: ${({ theme }) => theme.colors.backgroundElevated};
`;

const HeadText = styled.div`
  flex: 1;
  min-width: 0;
`;

/** Лист и панель правки рядом: лист занимает всё, что осталось от панели. */
const Body = styled.div`
  flex: 1;
  min-height: 0;
  display: flex;
`;

const Main = styled.main`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
`;

/** Панель правки строки №8 (выпуск 4) — справа от листа. */
const Side = styled.aside`
  width: 440px;
  max-width: 45%;
  flex-shrink: 0;
  overflow: auto;
  padding: ${({ theme }) => theme.spacing(3)};
  border-left: 1px solid ${({ theme }) => theme.colors.border};
  background: ${({ theme }) => theme.colors.backgroundElevated};
  font-size: 13.5px;
`;

const SideTitle = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing(2)};
  font-size: 13px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const Place = styled.div`
  font-size: 13px;
  color: ${({ theme }) => theme.colors.text.secondary};
  overflow-wrap: anywhere;
`;

const Title = styled.div`
  margin-top: 2px;
  font-size: 15px;
  font-weight: 600;
  overflow-wrap: anywhere;
`;

const Qty = styled.span`
  margin-left: 8px;
  font-weight: 400;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const Message = styled.div<{ $error?: boolean }>`
  padding: ${({ theme }) => theme.spacing(6)};
  font-size: 14px;
  line-height: 1.5;
  color: ${({ $error, theme }) => ($error ? theme.colors.status.danger : theme.colors.text.secondary)};
`;

/** Окно исходного листа РД (пункт 9 плана ПТО): отдельное окно браузера
 *  `osnova_pto_rd_sheet` без меню ОСНОВЫ — его можно увести на второй
 *  монитор. Лист и строку задаёт экран «Позиции объёма»: адресом при открытии и
 *  сообщением `show` канала `osnova_pto_sheet_viewer` при следующих кликах.
 *
 *  Панель правки №8 (выпуск 4) — справа от листа, если в проекте включены
 *  правки РД и у пользователя есть право правки или «Спорно» (те же права, что
 *  на экране: `useRdVolumeEditAccess`). Строку окно берёт своим запросом
 *  (`…/volumes/{id}/history`, доступ к проекту проверяет сервер), а после
 *  правки шлёт экрану `pto_volume_changed` — экран перезапрашивает позиции. */
export function PtoRdSheetWindow() {
  const [target, setTarget] = useState<SheetTarget | null>(() =>
    targetFromSearch(window.location.search),
  );
  // Номер показа: повторный клик по той же строке снова прокручивает к ней.
  const [shown, setShown] = useState(0);
  const channel = useRef<BroadcastChannel | null>(null);
  const [panelOpen, setPanelOpen] = useState(true);

  // Канал синхронизации с экраном: `show` — новый лист/строка, `ping` — «есть
  // ли окно». Окно говорит `hello` при открытии и `bye` при закрытии.
  useEffect(() => {
    if (typeof BroadcastChannel === 'undefined') return;
    const ch = new BroadcastChannel(SHEET_CHANNEL);
    channel.current = ch;
    ch.onmessage = (e: MessageEvent) => {
      const m = parseSheetMessage(e.data);
      if (!m) return;
      if (m.type === 'show') {
        setTarget(m.target);
        setShown((n) => n + 1);
        // Перезагрузка окна — тот же лист.
        window.history.replaceState(
          window.history.state,
          '',
          `${SHEET_ROUTE}?${targetToSearch(m.target)}`,
        );
        ch.postMessage({ type: 'shown', v: SHEET_PROTOCOL, id: m.id });
        window.focus();
      } else if (m.type === 'ping') {
        ch.postMessage({ type: 'hello', v: SHEET_PROTOCOL });
      }
    };
    ch.postMessage({ type: 'hello', v: SHEET_PROTOCOL });
    const bye = () => ch.postMessage({ type: 'bye', v: SHEET_PROTOCOL });
    window.addEventListener('pagehide', bye);
    return () => {
      window.removeEventListener('pagehide', bye);
      bye();
      channel.current = null;
      ch.close();
    };
  }, []);

  // Панель правки №8: права — по проекту строки; строка — своим запросом.
  const rights = useRdVolumeEditAccess(target?.projectId ?? null);
  const canPanel = !!target?.positionId && rights.enabled && (rights.canEdit || rights.canDispute);
  const history = useGetPtoVolumeHistoryQuery(
    canPanel && target?.positionId ? target.positionId : skipToken,
  );
  // Пока ответ по новой строке не пришёл, у запроса прежние данные — их не
  // показываем: строка панели всегда та, что открыта на листе.
  const position =
    history.data &&
    history.data.position.id === target?.positionId &&
    history.data.position.revision_id === target.revisionId
      ? history.data.position
      : null;
  const revisionId = target?.revisionId ?? null;
  // Экрану — только id ревизии: позиции он перезапросит сам.
  const notifyChanged = useCallback(() => {
    if (!revisionId) return;
    const msg: SheetMessage = { type: 'pto_volume_changed', v: SHEET_PROTOCOL, revisionId };
    channel.current?.postMessage(msg);
  }, [revisionId]);

  useEffect(() => {
    const prev = document.title;
    document.title = target
      ? `${target.place || `стр. PDF ${target.page}`} — лист РД · ОСНОВА`
      : 'Лист РД · ОСНОВА';
    return () => {
      document.title = prev;
    };
  }, [target]);

  const sheetArgs = target
    ? { revisionId: target.revisionId, page: target.page, file: target.file }
    : skipToken;
  const sheet = useGetPtoRdSheetImageQuery(sheetArgs);
  const cont = target && isContinuation(target) && target.headPage !== null;
  const head = useGetPtoRdSheetImageQuery(
    cont && target?.headPage
      ? { revisionId: target.revisionId, page: target.headPage, file: target.file }
      : skipToken,
  );

  return (
    <Page>
      <Head>
        <HeadText>
          <Place>
            {target
              ? [target.docCode, target.place || `стр. PDF ${target.page}`]
                  .filter(Boolean)
                  .join(' · ')
              : 'Окно исходного листа РД'}
          </Place>
          {target?.title && (
            <Title>
              {target.title}
              {target.qty && <Qty>{target.qty}</Qty>}
            </Title>
          )}
        </HeadText>
        {canPanel && !panelOpen && (
          <Button $variant="secondary" $size="sm" onClick={() => setPanelOpen(true)}>
            Правка строки
          </Button>
        )}
      </Head>
      <Body>
        <Main>
          {!target ? (
            <Message>
              Нажмите номер строки, «Источник» или отметку «✓» на экране «Позиции объёма» — лист РД
              откроется здесь. Окно можно перенести на второй монитор: следующие строки откроются в
              нём же.
            </Message>
          ) : sheet.isError ? (
            <Message $error>{apiErrorText(sheet.error, 'Не удалось показать лист РД.')}</Message>
          ) : !sheet.data || sheet.isFetching ? (
            <Message>Лист загружается…</Message>
          ) : (
            <SheetView target={target} shown={shown} image={sheet.data} headImage={head.data} />
          )}
        </Main>
        {canPanel && panelOpen && target && (
          <Side aria-label="Правка строки">
            <SideTitle>Правка строки по этому листу</SideTitle>
            {history.isError ? (
              <Message $error>{apiErrorText(history.error, 'Строка не загрузилась.')}</Message>
            ) : !position ? (
              <Message>Строка загружается…</Message>
            ) : (
              <RowEditPanel
                key={position.id}
                position={position}
                rights={rights}
                initial={looksUnrecognized(position).length > 0 ? 'fix' : 'history'}
                sheetHint={sheetHintOf(target)}
                onChanged={notifyChanged}
                onClose={() => setPanelOpen(false)}
              />
            )}
          </Side>
        )}
      </Body>
    </Page>
  );
}
